import React, { useCallback, useContext, useEffect, useState } from "react";
import { FireBaseContext } from "../../Context/FireBase";
import { getDocs } from "firebase/firestore";
import { TablePagination, TextField } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import * as XLSX from "xlsx";

function CitiesReport() {
  const { Cities, EventRefrence, SubscribersRefrence } =
    useContext(FireBaseContext);
  const [item, setItem] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [eventCounts, setEventCounts] = useState({});
  const [subscriberCounts, setSubscriberCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Get cities data
      const citiesSnapshot = await getDocs(Cities);
      const citiesData = citiesSnapshot.docs[0]?.data()?.data || [];
      const formattedCities = citiesData.map((city) => ({ name: city }));
      setItem(formattedCities);

      // Create a map for faster lookups
      const cityMap = {};
      formattedCities.forEach((city) => {
        cityMap[city.name] = 0;
      });

      // Get events data
      const eventsSnapshot = await getDocs(EventRefrence);
      const eventCityCounts = { ...cityMap };

      eventsSnapshot.docs.forEach((doc) => {
        const city = doc.get("city");
        if (eventCityCounts.hasOwnProperty(city)) {
          eventCityCounts[city] += 1;
        }
      });
      setEventCounts(eventCityCounts);

      // Get subscribers data
      const subscribersSnapshot = await getDocs(SubscribersRefrence);
      const subscriberCityCounts = { ...cityMap };

      subscribersSnapshot.docs.forEach((doc) => {
        const city = doc.get("city");
        if (subscriberCityCounts.hasOwnProperty(city)) {
          subscriberCityCounts[city] += 1;
        }
      });
      setSubscriberCounts(subscriberCityCounts);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
      setLoading(false);
    }
  }, [Cities, EventRefrence, SubscribersRefrence]);

  useEffect(() => {
    fetchData();
  }, []);

  const filteredRows = item.filter((city) => {
    return selectedCity ? city.name === selectedCity : true;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = (data) => {
    // Create a new workbook and a new worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "All Users");

    let filename = "cities_data.xlsx";

    if (selectedCity) {
      filename = `${selectedCity}.xlsx`;
    }
    XLSX.writeFile(wb, filename);
  };

  const handleExport = () => {
    // Call the export function with the filtered rows
    const excelFilter = filteredRows.map((row) => {
      return {
        "City Name": row.name,
        "Total Events": eventCounts[row.name],
        "Total Subscribers": subscriberCounts[row.name],
      };
    });
    if (excelFilter.length) {
      exportToExcel(excelFilter);
    } else {
      toast.error("There is No data to Export", { autoClose: 4000 });
    }
  };

  return (
    <div className="d-flex flex-column container-fluid container-md gap-3">
      <ToastContainer />
      <h2 className="mb-4">Cities Report</h2>
      {loading ? (
        <div
          className="w-100 flex justify-center items-center"
          style={{ height: "calc(100vh - 250px) " }}
        >
          <div className="dot-spinner">
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
            <div className="dot-spinner__dot"></div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-3 rounded-md ">
          <div className="flex justify-between items-center">
            <div className="w-80">
              <label
                className="block text-black text-sm font-bold mb-2 w-fit"
                htmlFor="select2"
              >
                Filter City
              </label>
              <TextField
                select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                className="border-bottom w-100"
              >
                <option value="">All </option>
                {item.map((city, index) => (
                  <option key={index} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </TextField>
            </div>
            <div className="px-3">
              <button
                onClick={handleExport}
                className="btn btn-success text-white"
              >
                <i className="fa-solid fa-file-arrow-down fs-5 text-white mr-2"></i>
                <span>EXPORT</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto mt-4 mb-6 ">
            <table className="min-w-full bg-white  rounded-lg  text-center ">
              <thead className=" text-black  text-lg border-b border-gray-200">
                <tr>
                  <th className="w-1/6 py-3 px-4">City Name</th>
                  <th className="w-1/6 py-3 px-4">Total Events</th>
                  <th className="w-1/6 py-3 px-4">Total Subscribers</th>
                </tr>
              </thead>
              <tbody className="">
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((itemList, index) => (
                    <tr
                      key={index}
                      className="text-blueColor font-normal border-b  odd:bg-white  even:bg-gray-100  "
                    >
                      <td className="py-3 px-4">{itemList.name}</td>
                      <td className="py-3 px-4">
                        {eventCounts[itemList.name]}
                      </td>
                      <td className="py-3 px-4">
                        {subscriberCounts[itemList.name]}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <div className="my-3">
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CitiesReport;

// const getData = (CollectionType, SetItem) => {
//   onSnapshot(CollectionType, (snapshot) => {
//     const newData = snapshot.docs.map((doc) => ({
//       ...doc.data(),
//     }));

//     const formattedData = Array.isArray(newData[0]?.data)
//       ? newData[0].data.map((item) => ({ name: item }))
//       : [];

//     SetItem(formattedData);
//     setLoaded(true);
//   });
// };

// const getEventsData = useCallback(() => {
//   onSnapshot(EventRefrence, (snapshot) => {
//     const eventsData = snapshot.docs.map((doc) => doc.data());

//     const cityEventCounts = item.reduce((acc, city) => {
//       acc[city.name] = 0;
//       return acc;
//     }, {});

//     eventsData.forEach((event) => {
//       const eventCity = event.city;
//       if (eventCity && cityEventCounts.hasOwnProperty(eventCity)) {
//         cityEventCounts[eventCity] += 1;
//       }
//     });

//     setEventCounts(cityEventCounts);
//     setEventsLoaded(true);
//   });
// }, [item, EventRefrence]);

// const getSubscribersData = useCallback(() => {
//   onSnapshot(SubscribersRefrence, (snapshot) => {
//     const subscribersData = snapshot.docs.map((doc) => doc.data());

//     const citySubscriberCounts = item.reduce((acc, city) => {
//       acc[city.name] = 0;
//       return acc;
//     }, {});

//     subscribersData.forEach((subscriber) => {
//       const subscriberCity = subscriber.city;
//       if (
//         subscriberCity &&
//         citySubscriberCounts.hasOwnProperty(subscriberCity)
//       ) {
//         citySubscriberCounts[subscriberCity] += 1;
//       }
//     });

//     setSubscriberCounts(citySubscriberCounts);
//     setSubscribersLoaded(true);
//   });
// }, [item, SubscribersRefrence]);

// useEffect(() => {

//   getData(Cities, setItem);
// }, [Cities]);

// const getEventsData = useCallback(() => {
//   onSnapshot(EventRefrence, (snapshot) => {
//     const cityList = snapshot.docs.map((doc) => doc.get("city"));

//     const cityEventCounts = item.reduce((acc, city) => {
//       acc[city.name] = 0;
//       return acc;
//     }, {});

//     cityList.forEach((eventCity) => {
//       if (cityEventCounts.hasOwnProperty(eventCity)) {
//         cityEventCounts[eventCity] += 1;
//       }
//     });

//     setEventCounts(cityEventCounts);
//     setEventsLoaded(true);
//   });
// }, [item, EventRefrence]);

// const getSubscribersData = useCallback(() => {
//   onSnapshot(SubscribersRefrence, (snapshot) => {
//     const subscribersData = snapshot.docs.map((doc) => doc.get("city"));

//     const citySubscriberCounts = item.reduce((acc, city) => {
//       acc[city.name] = 0;
//       return acc;
//     }, {});

//     subscribersData.forEach((subscriberCity) => {
//       if (citySubscriberCounts.hasOwnProperty(subscriberCity)) {
//         citySubscriberCounts[subscriberCity] += 1;
//       }
//     });

//     setSubscriberCounts(citySubscriberCounts);
//     setSubscribersLoaded(true);
//   });
// }, [item, SubscribersRefrence]);

// useEffect(() => {
//   if (item.length > 0) {
//     getEventsData();
//     getSubscribersData();
//   }
// }, [item]);

// useEffect(() => {
//   if (Loaded && eventsLoaded && subscribersLoaded) {
//     setLoading(false);
//   }
// }, [Loaded, eventsLoaded, subscribersLoaded]);
